import { filter } from 'rxjs';

import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WebsocketMessageService } from '@mitsubishi/core';

import { ApplyPatchWsService } from '../ws/apply-patch/apply-patch-ws.service';
import { StudyWsService } from '../ws/study/study-ws.service';
import { StudyNextStore } from './study-next.store';

export interface StudyNextRealtimeOptions {
  /** The study this page shows — permanent or temporary; the topic and the patches are the same either way. */
  studyId: string;

  /** The study is gone: the page decides where its user goes. */
  onDeleted: () => void;
}

/**
 * Everything that keeps a server-side study page current, for the two pages that render one: the study
 * topic, the JSON-patch signal, and the reconnect re-read. Called from a component constructor, which
 * is an injection context — `rxMethod` warns when called from `ngOnInit`, and subscribing before the
 * page's own load means nothing that lands in between is missed. Torn down with the caller.
 *
 * The store is the page's (`providers`), so each page has its own subscription and its own teardown.
 */
export function connectStudyNextRealtime({ studyId, onDeleted }: StudyNextRealtimeOptions): void {
  const store = inject(StudyNextStore);
  const studyWs = inject(StudyWsService);
  const applyPatchWs = inject(ApplyPatchWsService);
  const wsMessages = inject(WebsocketMessageService);
  const destroyRef = inject(DestroyRef);

  // Realtime (`PLAN.md` §4 P3): every study event, the page's own writes included, goes to the store,
  // which re-reads what the event names (`planRefresh`).
  store.onStudyEvent(studyWs.events$);

  studyWs.subscribeTopic(studyId);
  destroyRef.onDestroy(() => studyWs.unsubscribeTopic());

  studyWs.events$
    .pipe(
      filter(event => event.type === 'STUDY_DELETED'),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe(onDeleted);

  // Events sent while the socket was down are gone: re-read everything when it comes back (`PLAN.md` §6.9).
  //
  // Guarded on the scaffold having landed at least once. `reconnected$` also tells a subscriber that
  // joined during an outage when the connection is up, first connection included — deliberate, and
  // specified in `websocket-message.service.spec.ts`. This wiring runs from the page's constructor, so on
  // a browser reload straight onto a study URL it subscribes while the socket is still opening: without
  // the guard, the socket coming up would re-read everything the page's own `loadStudy` is loading at that
  // very moment. Before the first scaffold there is nothing stale to re-read; after it, there is.
  wsMessages.reconnected$
    .pipe(
      filter(() => store.counters() !== null),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe(() => store.refreshAll());

  // The only notice a server-side page gets that a mutation landed: it is server-side, and the writes it
  // issues reach no NGXS action. The patch is a signal to refetch, never applied here —
  // `PAGINATION.md` §7. Counters and the good-for-selection card follow every write; the grid re-queries
  // only when what its rows are drawn from changed, and in place — `invalidateRows` keeps the scroll position. A
  // change that reaches no row (a rename, a note) needs nothing more: the same patch refreshes the detail
  // in `ProjectStudiesStore`, which `store.detail()` reads. Not debounced: the store's own `switchMap`
  // collapses concurrent refreshes.
  applyPatchWs
    .studyChanges$(studyId)
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(change => {
      if (change.kind === 'deleted') {
        onDeleted();
        return;
      }

      store.refreshScaffold();
      if (change.rows) {
        store.invalidateRows({ withDetail: true });
      }
    });
}
